import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import dayjs from "dayjs";

/* ────────────────────────── Styles ────────────────────────── */
// Shared glass look (mirrors CustomerSmsReport / DownloadCenter)
const filterFieldSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

// Date pickers: 140px (matches the other report pages)
const datePickerSx = {
  width: 140,
  ...filterFieldSx,
};

// Select fields: 140px, glass + hover/focus glow
const selectSx = {
  width: 140,
  ...filterFieldSx,
  "& .MuiOutlinedInput-root": {
    ...filterFieldSx["& .MuiOutlinedInput-root"],
    borderRadius: "8px",
    height: 35,
    background: "rgba(255, 255, 255, 0.8)",
    transition: "all 0.2s ease",
    "&:hover": { boxShadow: "0 4px 12px rgba(14,165,233,0.1)" },
    "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(14,165,233,0.2)" },
  },
  "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#475569" },
};

const ConsolidateReport = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [senderID, setSenderID] = useState("ALL");
  const [type, setType] = useState("WEB");

  const tableData = [
    {
      createDate: "26/07/2023 17:50:18 PM",
      fromDate: "26/07/2023",
      toDate: "26/07/2023",
      senderID: "ALL",
      type: "WEB",
    },
    {
      createDate: "23/08/2023 12:52:00 PM",
      fromDate: "01/08/2023",
      toDate: "23/08/2023",
      senderID: "ALL",
      type: "WEB",
    },
  ];

  return (
    <Box sx={layoutCSS}>
      {/* Page title */}
      <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}>
        Month Wise Report
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* From Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              maxDate={dayjs()}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: datePickerSx } }}
            />
          </LocalizationProvider>

          {/* To Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              maxDate={dayjs()}
              minDate={toDate}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: datePickerSx } }}
            />
          </LocalizationProvider>

          {/* Sender ID Dropdown */}
          <FormControl size="small" sx={selectSx}>
            <InputLabel id="sender-id-label" sx={{ fontSize: "0.75rem" }}>
              Sender ID
            </InputLabel>
            <Select
              labelId="sender-id-label"
              label="Sender ID"
              value={senderID}
              onChange={(e) => setSenderID(e.target.value)}
            >
              <MenuItem value="ALL" sx={{ fontSize: "0.75rem", py: 0.5 }}>ALL</MenuItem>
              <MenuItem value="ID1" sx={{ fontSize: "0.75rem", py: 0.5 }}>ID1</MenuItem>
              <MenuItem value="ID2" sx={{ fontSize: "0.75rem", py: 0.5 }}>ID2</MenuItem>
            </Select>
          </FormControl>

          {/* Type Dropdown */}
          <FormControl size="small" sx={selectSx}>
            <InputLabel id="type-label" sx={{ fontSize: "0.75rem" }}>
              Type
            </InputLabel>
            <Select
              labelId="type-label"
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <MenuItem value="WEB" sx={{ fontSize: "0.75rem", py: 0.5 }}>WEB</MenuItem>
              <MenuItem value="SMS" sx={{ fontSize: "0.75rem", py: 0.5 }}>API</MenuItem>
            </Select>
          </FormControl>

          {/* Submit Button */}
          <Button
            variant="contained"
            size="small"
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              bgcolor: "#0288d1",
              "&:hover": { bgcolor: "#0277bd" },
              minWidth: "130px",
            }}
          >
            Submit Request
          </Button>
        </Box>
      </Paper>

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                color: "white",
                fontSize: "0.8rem",
                padding: "6px",
                borderBottom: "0.5px solid #1b1b1bff",
                borderRight: "0.5px solid #1b1b1bff",
              },
            }}
          >
            <TableRow>
              <TableCell>Create Date</TableCell>
              <TableCell>From Date</TableCell>
              <TableCell>To Date</TableCell>
              <TableCell>SenderID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => (
                <TableRow key={index} sx={{ height: "36px" }}>
                  <TableCell sx={cellSX}>{row.createDate}</TableCell>
                  <TableCell sx={cellSX}>{row.fromDate}</TableCell>
                  <TableCell sx={cellSX}>{row.toDate}</TableCell>
                  <TableCell sx={cellSX}>{row.senderID}</TableCell>
                  <TableCell sx={cellSX}>{row.type}</TableCell>
                  <TableCell sx={cellSX}>
                    <IconButton
                      sx={{ color: "#1976d2", p: 0.5 }}
                      onClick={() => alert("Download action")}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      sx={{ color: "red", p: 0.5 }}
                      onClick={() => alert("Delete action")}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={cellSX} colSpan={6} align="center">
                  No Data Found!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ConsolidateReport;